import { errorHelpers } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { schemas } from 'modules/data';
import { useCallback, useEffect, useMemo } from 'react';
import type { MatchingInfoNormalizer } from 'shared/components';
import {
    LineItemNormalizer,
    qBooksMatchingPopupAccountLineItemNormalizerState,
} from 'shared/components/QBooksMatchingPopup';
import { useCompanyBasedOnUrl, useCompanyBetaFeatures } from 'shared/hooks';

import { ExpandedQBooksAccountLineItem } from '../../../../../selectors/types/ExpandedQBooksAccountLineItem';
import { useConstructAccountLineItem } from './useConstructAccountLineItem';
import { useRequestExpanded } from './useRequest';

export const useNormalize = (accountLineItems: ExpandedQBooksAccountLineItem[]) => {
    const company = useCompanyBasedOnUrl();
    const { isQBOMatchingOnEditing } = useCompanyBetaFeatures(company.id);

    const request = useRequestExpanded();

    const { constructAccountLineItem } = useConstructAccountLineItem(request, accountLineItems);

    const setAccountLineItemNormalizer = useSetAtom(qBooksMatchingPopupAccountLineItemNormalizerState);

    const amountSumPerAccount = useMemo(
        () =>
            accountLineItems.reduce<Record<string, number>>((acc, cur) => {
                if (cur.matchedLineItemId) {
                    acc[cur.matchedLineItemId] = (acc[cur.matchedLineItemId] || 0) + (cur.amount ?? 0);
                }

                return acc;
            }, {}),
        [accountLineItems]
    );

    const accountLineItemNormalizer = useCallback<LineItemNormalizer<ReturnType<typeof schemas.mapAccountLineItem>>>(
        (value) => {
            return {
                ...value,
                remainingBalanceOrig: value.remainingBalance,
                remainingBalance: (value.remainingBalanceForRequest || 0) - (amountSumPerAccount?.[value.id] || 0),
            };
        },
        [amountSumPerAccount]
    );

    const matchingInfoNormalizer = useCallback<MatchingInfoNormalizer>(
        async (value, lineItemId) => {
            if (!isQBOMatchingOnEditing) {
                return value;
            }

            // a case:
            // - initially we have an unmatched line.
            // - we match it w/ some PO
            // - we see that the icon changed to "matched", which can be used
            // to display a popup w/ matching info
            // - we are open the popup and AT THIS POINT BE don't know about
            // the fact that locally line was matched
            //
            // Thus, our goal here is to fill the matched line from by the data,
            // we've gathered
            const gatheredValue = value?.id ? value : await constructAccountLineItem(lineItemId);

            if (!gatheredValue || !gatheredValue.accountLineItem) {
                throw errorHelpers.notFoundError();
            }

            return {
                ...gatheredValue,
                accountLineItem: accountLineItemNormalizer(gatheredValue.accountLineItem),
            };
        },
        [accountLineItemNormalizer, constructAccountLineItem, isQBOMatchingOnEditing]
    );

    useEffect(() => {
        setAccountLineItemNormalizer(accountLineItemNormalizer);
    }, [accountLineItemNormalizer, setAccountLineItemNormalizer]);

    return matchingInfoNormalizer;
};
